import { Link } from "react-router-dom";
import SharedLayout from "../components/SharedLayout";
import useSEO from "../utils/useSEO";
import { HeroIllo, MobileIllo } from "../components/AnimatedIllustrations";

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);
const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
    <path d="M5 13l4 4L19 7" />
  </svg>
);
const GlowOrb = ({ className }) => (
  <div className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`} />
);

const PLATFORMS = [
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
      </svg>
    ),
    title: "iOS Development",
    desc: "Pixel-perfect native iOS apps built with Swift and SwiftUI. Optimised for performance, App Store guidelines, and Apple's Human Interface Guidelines.",
    color: "border-slate-500/30 bg-slate-500/5",
    accent: "text-slate-300",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
        <circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
    title: "Android Development",
    desc: "Native Android apps using Kotlin and Jetpack Compose. Material Design 3, deep device integration, and Play Store ready.",
    color: "border-emerald-500/30 bg-emerald-500/5",
    accent: "text-emerald-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <circle cx="12" cy="12" r="2" fill="#61DAFB" stroke="none" />
        <ellipse cx="12" cy="12" rx="9.5" ry="3.5" stroke="#61DAFB" strokeWidth="1.2" />
        <ellipse cx="12" cy="12" rx="9.5" ry="3.5" stroke="#61DAFB" strokeWidth="1.2" transform="rotate(60 12 12)" />
        <ellipse cx="12" cy="12" rx="9.5" ry="3.5" stroke="#61DAFB" strokeWidth="1.2" transform="rotate(120 12 12)" />
      </svg>
    ),
    title: "Cross-Platform (React Native)",
    desc: "One codebase, two platforms. React Native apps with native performance, shared logic, and platform-specific UI polish.",
    color: "border-cyan-500/30 bg-cyan-500/5",
    accent: "text-cyan-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M4.5 12a7.5 7.5 0 0 0 15 0m-15 0a7.5 7.5 0 1 1 15 0m-15 0H3m16.5 0H21m-1.5 0H12m-8.457 3.077 1.41-.513m14.095-5.13 1.41-.513M5.106 17.785l1.15-.964m11.49-9.642 1.149-.964M7.501 19.795l.75-1.3m7.5-12.99.75-1.3m-6.063 16.658.26-1.477m2.605-14.772.26-1.477m0 17.726-.26-1.477M10.698 4.614l-.26-1.477M16.5 19.794l-.75-1.299M7.5 4.205 6.75 2.9m-.439 16.556-.263-1.477" />
      </svg>
    ),
    title: "Cross-Platform (Flutter)",
    desc: "Flutter apps with Dart for pixel-consistent UI across iOS, Android, and beyond. Ideal for brand-heavy, design-forward products.",
    color: "border-blue-500/30 bg-blue-500/5",
    accent: "text-blue-400",
  },
];

const PROCESS = [
  {
    step: "01",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
      </svg>
    ),
    title: "Discovery & Architecture",
    desc: "We map your user journeys, define the tech architecture, and choose the right platform strategy before writing a single line of code.",
  },
  {
    step: "02",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M9.53 16.122a3 3 0 0 0-5.78 1.128 2.25 2.25 0 0 1-2.4 2.245 4.5 4.5 0 0 0 8.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 0 0 3.388-1.62m-5.043-.025a15.994 15.994 0 0 1 1.622-3.395m3.42 3.42a15.995 15.995 0 0 0 4.764-4.648l3.876-5.814a1.151 1.151 0 0 0-1.597-1.597L14.146 6.32a15.996 15.996 0 0 0-4.649 4.763m3.42 3.42a6.776 6.776 0 0 0-3.42-3.42" />
      </svg>
    ),
    title: "UI/UX Design",
    desc: "High-fidelity Figma prototypes aligned to platform guidelines. We test with real users before handing off to engineering.",
  },
  {
    step: "03",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M14.25 9.75L16.5 12l-2.25 2.25m-4.5 0L7.5 12l2.25-2.25M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25z" />
      </svg>
    ),
    title: "Agile Development",
    desc: "Two-week sprints with working builds every cycle. You see real progress, not slideshows. QA is embedded, not an afterthought.",
  },
  {
    step: "04",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
      </svg>
    ),
    title: "Launch & Scale",
    desc: "Store submission, phased rollouts, crash monitoring, and post-launch iteration. We stay the course until your metrics are green.",
  },
];

const TECH = [
  "React Native", "Flutter", "Swift / SwiftUI", "Kotlin / Jetpack",
  "Expo", "Firebase", "GraphQL", "WebRTC", "Stripe SDK",
  "Push Notifications", "ARKit / ARCore", "App Store / Play",
];

const FEATURES = [
  "Offline-first architecture",
  "Biometric authentication (FaceID / Fingerprint)",
  "Real-time push notifications",
  "In-app purchases & subscriptions",
  "Deep linking & universal links",
  "App Store & Play Store submission",
  "Crash reporting & analytics",
  "CI/CD with Fastlane & Bitrise",
  "A/B testing & feature flags",
  "Accessibility (WCAG 2.1 AA)",
  "Localisation & RTL support",
  "End-to-end encryption",
];

export default function MobileAppsDetail() {
  useSEO({
    title: "Mobile App Development Company in Texas | BerryByte",
    description: "Custom mobile application development in Texas — native iOS & Android, React Native & Flutter apps. BerryByte takes you from MVP to millions of downloads.",
    keywords: "custom mobile application development, mobile app development company in texas, best mobile application development company in texas, ios app development services in texas, android app development service in texas",
    canonical: "https://www.berrybyteinn.com/services/mobile-apps",
  });

  return (
    <SharedLayout>
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-20">
        <GlowOrb className="w-[700px] h-[700px] bg-cyan-600 -top-32 -right-48" />
        <GlowOrb className="w-[500px] h-[500px] bg-violet-700 bottom-0 -left-32" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

        {/* decorative animated illustration */}
        <HeroIllo>
          <MobileIllo />
        </HeroIllo>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <a href="/services" className="inline-flex items-center gap-2 text-slate-500 hover:text-cyan-400 text-sm font-medium transition-colors duration-200 mb-8">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
            Services / Mobile Development
          </a>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 text-sm font-medium mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
              iOS · Android · React Native · Flutter
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight mb-6">
              Mobile Apps<br />
              <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Built to Ship.</span>
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl mb-10">
              From MVP to millions of downloads — we design, build, and launch mobile apps that users love and businesses rely on. Native quality, cross-platform speed.
            </p>
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-cyan-600 to-teal-600 text-white font-semibold shadow-2xl shadow-cyan-900/40 hover:shadow-cyan-900/60 hover:-translate-y-1 transition-all duration-300">
              Start Your App<span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
            </Link>

            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8 pt-12 border-t border-white/5">
              {[{ value: "60+", label: "Apps Shipped" }, { value: "4.8", label: "Avg Store Rating" }, { value: "iOS & Android", label: "Both Platforms" }, { value: "8 wks", label: "Avg MVP Timeline" }].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-2xl lg:text-3xl font-black text-white mb-1">{s.value}</div>
                  <div className="text-sm text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[500px] h-[500px] bg-cyan-700 -top-24 -right-32" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-cyan-400 font-semibold text-sm tracking-widest uppercase">Platforms</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">Every platform,<br /><span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">done right.</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {PLATFORMS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className={`rounded-2xl border ${p.color} p-7 hover:-translate-y-1 transition-transform duration-300`}>
                  <div className={`inline-flex p-3 rounded-xl bg-white/5 border border-white/10 ${p.accent} mb-4`}><Icon /></div>
                  <h3 className={`text-lg font-bold mb-2 ${p.accent}`}>{p.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#050810] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-teal-400 font-semibold text-sm tracking-widest uppercase">How We Work</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">Our process, no surprises.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.step} className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 hover:-translate-y-1 transition-transform duration-300">
                  <div className="inline-flex p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 mb-4"><Icon /></div>
                  <div className="text-xs font-bold text-cyan-500 tracking-widest mb-2">{step.step}</div>
                  <h3 className="text-base font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-cyan-400 font-semibold text-sm tracking-widest uppercase">Technology</span>
              <h2 className="mt-4 text-3xl lg:text-4xl font-black text-white mb-8">The stack behind the magic.</h2>
              <div className="flex flex-wrap gap-3">
                {TECH.map((t) => (
                  <span key={t} className="px-4 py-2 rounded-xl border border-cyan-500/20 bg-cyan-500/5 text-cyan-300 text-sm font-medium hover:bg-cyan-500/10 transition-colors">
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="text-teal-400 font-semibold text-sm tracking-widest uppercase">What's Included</span>
              <h2 className="mt-4 text-3xl lg:text-4xl font-black text-white mb-8">Everything you need.</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {FEATURES.map((f) => (
                  <div key={f} className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-cyan-400"><Check /></span>
                    <span className="text-slate-300 text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[700px] h-[700px] bg-cyan-700 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-6xl font-black text-white mb-6">Got an app idea?<br /><span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Let's build it.</span></h2>
          <p className="text-slate-400 text-lg mb-10 leading-relaxed">
            We'll take it from concept to the App Store. Book a free 30-minute discovery call.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-cyan-600 to-teal-600 text-white font-semibold shadow-2xl shadow-cyan-900/40 hover:shadow-cyan-900/60 hover:-translate-y-1 transition-all duration-300">
              Book a Discovery Call<span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
            </Link>
            <a href="/services" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl border border-white/10 text-white font-semibold bg-white/5 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
              Explore Other Services
            </a>
          </div>
        </div>
      </section>
    </SharedLayout>
  );
}
